<template>
  <div class="min-h-screen p-4 md:p-6 lg:p-8">
    <div
      class="w-full max-w-7xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow-lg"
    >
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          Device Management
        </h1>
        <p class="text-sm sm:text-base text-gray-600">
          Manage and monitor all devices in your system
        </p>
      </div>

      <!-- School Filter & Search Bar -->
      <div class="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <select
          v-model="selectedSchoolId"
          @change="handleSchoolChange"
          class="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="">All Schools</option>
          <option
            v-for="school in schools"
            :key="school._id"
            :value="school._id"
          >
            {{ school.schoolName }}
          </option>
        </select>

        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by chipID or device name..."
          class="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          @input="handleSearch"
        />
        <button
          @click="fetchDevices"
          class="w-full sm:w-auto px-4 sm:px-6 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          :disabled="isLoading"
        >
          {{ isLoading ? "Loading..." : "Refresh" }}
        </button>
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
      >
        {{ errorMessage }}
      </div>

      <!-- Loading State -->
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-12"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
        ></div>
      </div>

      <!-- Devices Display Section -->
      <div v-else-if="devices.length > 0">
        <!-- Desktop Table View -->
        <div class="hidden md:block overflow-x-auto">
          <table class="w-full text-left border-collapse text-sm">
            <thead>
              <tr class="bg-gray-100 border-b-2 border-gray-300">
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Device Name
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Code
                </th>

                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Status
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Last IP
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  MAC Address
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Chip ID
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Last Ping
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  URL
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Active
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Created
                </th>
                <th
                  class="px-3 lg:px-4 py-3 font-semibold text-gray-700 text-xs lg:text-sm"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="device in devices"
                :key="device._id"
                class="border-b border-gray-200 hover:bg-gray-50 transition"
              >
                <td
                  class="px-3 lg:px-4 py-3 font-medium text-gray-900 text-xs lg:text-sm"
                >
                  {{ device.deviceName }}
                </td>
                <td class="px-3 lg:px-4 py-3 text-gray-600 font-mono text-xs">
                  {{ device.code }}
                </td>

                <td class="px-3 lg:px-4 py-3">
                  <span
                    class="px-2 py-1 rounded text-xs font-medium"
                    :class="getStatusClass(device.status)"
                  >
                    {{ device.status }}
                  </span>
                </td>
                <td class="px-3 lg:px-4 py-3 text-gray-600 font-mono text-xs">
                  {{ device.lastIp || "-" }}
                </td>
                <td class="px-3 lg:px-4 py-3 text-gray-600 font-mono text-xs">
                  {{ device.macAddress || "-" }}
                </td>
                <td class="px-3 lg:px-4 py-3 text-gray-600 font-mono text-xs">
                  {{ device.chipId || "-" }}
                </td>
                <td class="px-3 lg:px-4 py-3 text-gray-600 text-xs">
                  {{ formatDate(device.lastPing) }}
                </td>
                <td class="px-3 lg:px-4 py-3 text-blue-600 text-xs break-all">
                  <a
                    v-if="device.url"
                    :href="device.url"
                    target="_blank"
                    class="hover:underline"
                    >{{ device.url }}</a
                  >
                  <span v-else>-</span>
                </td>
                <td class="px-3 lg:px-4 py-3">
                  <span
                    class="px-2 py-1 rounded text-xs font-medium"
                    :class="
                      device.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    "
                  >
                    {{ device.isActive ? "Yes" : "No" }}
                  </span>
                </td>
                <td class="px-3 lg:px-4 py-3 text-gray-600 text-xs">
                  {{ formatDate(device.createdAt) }}
                </td>
                <td class="px-3 lg:px-4 py-3">
                  <button
                    @click="openEditModal(device)"
                    class="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile/Tablet Card View -->
        <div class="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            v-for="device in devices"
            :key="device._id"
            class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 hover:shadow-md transition"
          >
            <!-- Card Header -->
            <div
              class="flex items-start justify-between mb-3 pb-3 border-b border-gray-100"
            >
              <div class="flex-1">
                <h3 class="font-semibold text-gray-900 text-sm">
                  {{ device.deviceName }}
                </h3>
                <p class="text-xs text-gray-500 font-mono mt-1">
                  {{ device.code }}
                </p>
              </div>
              <span
                class="px-2 py-1 rounded text-xs font-medium whitespace-nowrap ml-2"
                :class="getStatusClass(device.status)"
              >
                {{ device.status }}
              </span>
            </div>

            <!-- Card Body -->
            <div class="space-y-2 text-xs">
              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">Last IP:</span>
                <span class="text-gray-900 font-mono">{{
                  device.lastIp || "-"
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">MAC:</span>
                <span class="text-gray-900 font-mono truncate">{{
                  device.macAddress || "-"
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">Chip ID:</span>
                <span class="text-gray-900 font-mono truncate">{{
                  device.chipId || "-"
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">Last Ping:</span>
                <span class="text-gray-900">{{
                  formatDate(device.lastPing)
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 font-medium">Active:</span>
                <span
                  class="px-2 py-0.5 rounded text-xs font-medium"
                  :class="
                    device.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  "
                >
                  {{ device.isActive ? "Yes" : "No" }}
                </span>
              </div>
              <div
                v-if="device.url"
                class="flex justify-between items-center pt-2 border-t border-gray-100"
              >
                <span class="text-gray-600 font-medium">URL:</span>
                <span class="text-gray-500 font-mono truncate">
                  {{ device.url }}
                </span>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="mt-4 pt-3 border-t border-gray-100">
              <button
                @click="openEditModal(device)"
                class="w-full px-3 py-2 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition"
              >
                Edit Device
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-12"
      >
        <p class="text-gray-500 text-sm md:text-base">No devices found</p>
      </div>

      <!-- Pagination -->
      <div
        v-if="devices.length > 0"
        class="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4"
      >
        <p class="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
          Showing <strong>{{ (currentPage - 1) * limit + 1 }}</strong> to
          <strong>{{ Math.min(currentPage * limit, totalDevices) }}</strong> of
          <strong>{{ totalDevices }}</strong>
        </p>
        <div class="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Previous
          </button>
          <span
            class="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-700 whitespace-nowrap"
            >Page {{ currentPage }}</span
          >
          <button
            @click="nextPage"
            :disabled="currentPage * limit >= totalDevices"
            class="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Next
          </button>
        </div>
      </div>

      <!-- Edit Modal -->
      <div
        v-if="showEditModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        @click.self="closeEditModal"
      >
        <div
          class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
          <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Edit Device
          </h2>

          <form
            @submit.prevent="saveDevice"
            class="space-y-4"
          >
            <!-- Device Name -->
            <div>
              <label class="block text-sm text-gray-700 font-medium mb-2"
                >Device Name</label
              >
              <input
                v-model="editForm.deviceName"
                type="text"
                class="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <!-- Code -->
            <div>
              <label class="block text-sm text-gray-700 font-medium mb-2"
                >Code</label
              >
              <input
                v-model="editForm.code"
                type="text"
                class="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <!-- Status -->
            <div>
              <label class="block text-sm text-gray-700 font-medium mb-2"
                >Status</label
              >
              <select
                v-model="editForm.status"
                class="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <!-- URL -->
            <div>
              <label class="block text-sm text-gray-700 font-medium mb-2"
                >URL</label
              >
              <input
                v-model="editForm.url"
                type="text"
                placeholder="https://example.com"
                class="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <!-- Active Toggle -->
            <div>
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  v-model="editForm.isActive"
                  type="checkbox"
                  class="w-4 h-4 accent-blue-600"
                />
                <span class="text-sm text-gray-700 font-medium"
                  >Mark as Active</span
                >
              </label>
            </div>

            <!-- Form Actions -->
            <div class="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                :disabled="isSaving"
                class="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {{ isSaving ? "Saving..." : "Save Changes" }}
              </button>
              <button
                type="button"
                @click="closeEditModal"
                class="flex-1 px-4 py-2 text-sm bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch, onUnmounted } from "vue";
  import { useStore } from "vuex";

  const store = useStore();

  const currentPage = ref(1);
  const limit = ref(20);
  const searchQuery = ref("");
  const selectedSchoolId = ref("");
  const isLoading = ref(false);
  const isSaving = ref(false);
  const errorMessage = ref("");
  const showEditModal = ref(false);
  const editingDeviceId = ref(null);

  const editForm = ref({
    deviceName: "",
    code: "",
    status: "active",
    url: "",
    isActive: true,
  });

  watch(
    () => editForm.value.status,
    (newStatus) => {
      if (newStatus === "disabled") {
        editForm.value.isActive = false;
      }
    },
  );

  watch(
    () => editForm.value.isActive,
    (newIsActive) => {
      if (!newIsActive) {
        editForm.value.status = "disabled";
      }
    },
  );

  const devices = computed(() =>
    (store.state.allDevices || []).slice().reverse(),
  );

  const totalDevices = computed(() => store.state.allDevicesTotal || 0);
  const schools = computed(() => store.state.schoolsBasic || []);

  const getStatusClass = (status) => {
    const statusMap = {
      active: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      disabled: "bg-red-100 text-red-800",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const fetchDevices = async () => {
    isLoading.value = true;
    errorMessage.value = "";

    try {
      const params = {
        page: currentPage.value,
        limit: limit.value,
        search: searchQuery.value,
      };

      if (selectedSchoolId.value) {
        params.schoolId = selectedSchoolId.value;
      }

      await store.dispatch("fetchAllDevices", params);
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message || "Failed to fetch devices";
      console.error("Fetch devices error:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const handleSchoolChange = () => {
    currentPage.value = 1;
    fetchDevices();
  };

  const handleSearch = () => {
    currentPage.value = 1;
    fetchDevices();
  };

  const nextPage = () => {
    if (currentPage.value * limit.value < totalDevices.value) {
      currentPage.value++;
      fetchDevices();
    }
  };

  const previousPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--;
      fetchDevices();
    }
  };

  const openEditModal = (device) => {
    editingDeviceId.value = device._id;
    editForm.value = {
      deviceName: device.deviceName,
      code: device.code,
      status: device.status,
      url: device.url,
      isActive: device.isActive,
    };
    showEditModal.value = true;
  };

  const closeEditModal = () => {
    showEditModal.value = false;
    editingDeviceId.value = null;
    editForm.value = {
      deviceName: "",
      code: "",
      status: "active",
      url: "",
      isActive: true,
    };
  };

  const saveDevice = async () => {
    isSaving.value = true;
    errorMessage.value = "";

    try {
      await store.dispatch("editDevice", {
        deviceId: editingDeviceId.value,
        updates: {
          deviceName: editForm.value.deviceName,
          code: editForm.value.code,
          status: editForm.value.status,
          url: editForm.value.url,
          isActive: editForm.value.isActive,
        },
      });

      closeEditModal();
      await fetchDevices();
    } catch (error) {
      errorMessage.value =
        error.response?.data?.message || "Failed to save device";
      console.error("Save device error:", error);
    } finally {
      isSaving.value = false;
    }
  };

  onMounted(() => {
    store.dispatch("fetchAllSchoolsBasic");
    fetchDevices();
  });

  onUnmounted(() => {
    // no auto refresh cleanup needed
  });
</script>
